import { useAuth } from "@/App";
import { Navigate } from "react-router-dom";
import {
  Server, Code2, Database, ShieldCheck, Sparkles, Cog, GitBranch,
  Package, Boxes, Braces, Layers, Zap, ArrowRight, ExternalLink,
  BookOpen, Route, Cloud, Lock,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

// ------------------------------------------------------------
// DEVELOPER NOTES — Guided tour of the Sterile Mate tech stack
// Visible only to accounts flagged as read_only_demo (permissions
// array contains "read_only_demo"). Route guard below enforces.
// ------------------------------------------------------------

const STACK = [
  {
    label: "Frontend",
    icon: Layers,
    accent: "sky",
    lines: [
      "React 19 (Create React App) — SPA served from /app/frontend",
      "Tailwind CSS + shadcn/ui component library (Radix primitives)",
      "React Router v6 — role-scoped route trees under /admin, /hospital, /supplier, /contractor",
      "Zustand + React Context for lightweight client state (auth, cart)",
      "lucide-react for iconography · sonner for toasts · recharts for analytics",
    ],
  },
  {
    label: "Backend",
    icon: Server,
    accent: "emerald",
    lines: [
      "FastAPI (Python 3.11) served by uvicorn on 0.0.0.0:8001 (supervisor-managed)",
      "Motor async MongoDB driver — single DB per environment (MONGO_URL + DB_NAME)",
      "PyJWT with HS256 · bcrypt password hashing · 24h JWT expiration",
      "Custom middlewares: SecurityHeaders (HIPAA-aligned), ReadOnlyDemoMiddleware (blocks writes for demo tokens)",
      "Router-based split under /app/backend/routers — see structure below",
    ],
  },
  {
    label: "Database",
    icon: Database,
    accent: "indigo",
    lines: [
      "MongoDB — one database per env (preview / production)",
      "String UUIDs used for public IDs; ObjectId only internal",
      "Collections: users, health_systems, health_system_users, suppliers, contractors, backorder_requests, campaign_orders, projects, work_orders, email_campaign_sends, telegram_verifications, team_invites, notifications, sessions, login_attempts, +80 more",
      "Full-text indexes on: suppliers.company_name, backorders.description, campaigns.subject",
      "Never returns raw _id — every read is mapped through BaseDocument.from_mongo() when needed",
    ],
  },
  {
    label: "Auth & Sessions",
    icon: Lock,
    accent: "amber",
    lines: [
      "JWT bearer tokens issued at POST /api/auth/login — payload: {user_id, email, role, demo_readonly?}",
      "Role hierarchy: super_admin > hub_admin > project_manager / sales_manager / compliance_officer / accountant / team_member > hospital_user / supplier / contractor",
      "Session records persisted in db.sessions with SHA-256 token hash (revocable)",
      "Rate limiter on login attempts by IP + email (prevents brute force)",
      "Demo reviewer token includes demo_readonly:true → intercepted by ReadOnlyDemoMiddleware on every non-GET",
    ],
  },
];

const INTEGRATIONS = [
  { name: "Emergent LLM Key",   what: "Universal key for Claude Sonnet 5 (text) + Gemini Nano Banana (image gen) + OpenAI GPT / Whisper / Image via emergentintegrations", where: "AskIsaac chat, GC hero image, dieline generation, product photo generation" },
  { name: "Microsoft Graph API", what: "Outbound email via authenticated M365 mailbox",                                                 where: "All CRM campaign sends, welcome emails, PO notifications, alerts" },
  { name: "Resend (fallback)",   what: "Backup transactional email provider",                                                          where: "Automatic fallback when Graph API is throttled or down" },
  { name: "Telegram Bot API",    what: "Webhook-driven ops signal bot (AskIsaac Signal)",                                              where: "Backorder alerts, supplier quote requests, work-order dispatch, 1-tap opt-in" },
  { name: "Twilio SMS",          what: "Two-way SMS on urgent work orders",                                                            where: "Emergency dispatch confirmation, contractor check-in reminders" },
  { name: "QuickBooks Online",   what: "Invoice + purchase order sync (OAuth flow scaffolded)",                                        where: "Marketplace orders, contractor invoices — currently manual push" },
  { name: "UPS Rating API",      what: "Live freight rate lookup",                                                                     where: "Marketplace checkout — currently PAUSED (awaiting credentials)" },
  { name: "Stripe",              what: "Card payments for AMMEX gloves + microfiber cash-pay DTC",                                      where: "Env test key ready; wiring pending" },
];

const ROUTERS = [
  { path: "auth.py",                    domain: "Login, refresh, logout, session mgmt, password reset" },
  { path: "public_signups.py",          domain: "Instant supplier + contractor self-registration (JWT on approval)" },
  { path: "team_signups.py",            domain: "Invite-only internal employee onboarding (/team/join)" },
  { path: "hospitals.py",               domain: "Hospital portal, hospital users, facility CRUD, access requests" },
  { path: "suppliers.py",               domain: "Supplier CRUD, quote requests, campaign alerts" },
  { path: "contractors.py",             domain: "Contractor CRUD, dispatch, GPS check-in/out, compliance docs" },
  { path: "backorders.py",              domain: "Backorder RFQs, supplier quote submissions, admin approvals" },
  { path: "marketplace.py",             domain: "Product catalog, PO uploads, artwork uploads, order flow" },
  { path: "orders.py",                  domain: "Campaign quote requests, admin order management" },
  { path: "emails.py",                  domain: "133+ email templates, sends, campaign scheduling, preview endpoints" },
  { path: "email_campaigns.py",         domain: "Bulk campaigns, department-filtered sends, audience preview" },
  { path: "crm.py",                     domain: "Contacts, tags, dedupe, import/export" },
  { path: "projects.py + project_command.py", domain: "Project Command Center, milestones, budget, audit logs" },
  { path: "pm_scheduler.py + pm_schedules.py", domain: "Preventive maintenance schedules, compliance summary, calendar" },
  { path: "telegram.py",                domain: "Bot webhook, verification codes, opt-in flow, signal alerts" },
  { path: "gloves.py + american_surgical_pricing.py + di_tanks.py + water/*", domain: "Product-line-specific pricing & catalog endpoints" },
  { path: "vendor_showcase.py",         domain: "Vendor showcase page CMS (logo, header, portfolio uploads)" },
  { path: "admin_crud.py + admin_verification.py", domain: "Generic admin CRUD utilities + doc-verification workflow" },
  { path: "trade_intelligence.py",      domain: "US sea-import record search (find consignees + overseas suppliers)" },
  { path: "public_orders.py + public_dashboard.py", domain: "Public-facing endpoints for unauthenticated flows (backorder ticket, PO uploads)" },
];

const HIGHLIGHTS = [
  { icon: Zap,        title: "AskIsaac AI Concierge",    body: "In-house assistant that parses hospital supply requests, parallel-quotes vendors, and creates work orders. Powered by Claude Sonnet 5 via emergentintegrations." },
  { icon: Sparkles,   title: "Nano Banana Image Gen",   body: "AI-generated product photography, dielines, hero images, and marketing photos. See /app/backend/scripts/gen_*.py for reproducible pipelines." },
  { icon: BookOpen,   title: "133+ Email Templates",     body: "All manifest-driven under /app/backend/email_templates/manifest.json. Rendered via /api/email-templates/{id}/preview with merge-tag substitution." },
  { icon: Route,      title: "Route-Scoped SPA",         body: "Every persona has its own route tree, layout, and portal shell — no shared UI leakage between hospital / supplier / contractor / admin." },
  { icon: Cloud,      title: "Emergent Object Storage",  body: "Static assets (email images, dieline PDFs, product photos) served via /api/static/... mounts. Object storage playbook for durable uploads." },
  { icon: ShieldCheck,title: "Compliance-First Auth",   body: "bcrypt hashing, 24h JWT rotation, session records, rate-limited login attempts, brute-force protection, HIPAA-aligned headers on every response." },
];

const AccentPill = ({ accent, children }) => {
  const map = {
    sky: "bg-sky-100 text-sky-800 border-sky-300",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${map[accent] || map.sky}`}>
      {children}
    </span>
  );
};

export default function DeveloperNotesPage() {
  const { user, token } = useAuth();
  const isDemo = user?.permissions?.includes("read_only_demo");

  // Still loading auth — render nothing rather than redirect
  if (token && !user) {
    return <div className="h-40 flex items-center justify-center text-sm text-slate-500">Loading&hellip;</div>;
  }

  // Hard gate — non-demo accounts get bounced to the dashboard
  if (!isDemo) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="space-y-6" data-testid="developer-notes-page">
      {/* Intro card */}
      <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-white">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-sky-400">
              <Code2 className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-2xl">Welcome, Developer.</CardTitle>
              <CardDescription className="text-sm">
                You are signed in as the read-only reviewer account. Every module is browsable; every write is intercepted server-side. This page is your guided tour of the Sterile Mate architecture.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Frontend</p>
              <p className="text-lg font-black text-slate-900">React 19 + Tailwind</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Backend</p>
              <p className="text-lg font-black text-slate-900">FastAPI + Motor</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Database</p>
              <p className="text-lg font-black text-slate-900">MongoDB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stack breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Boxes className="h-5 w-5 text-sky-600" /> Stack At A Glance
          </CardTitle>
          <CardDescription>Layer-by-layer summary of what runs where.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {STACK.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-700" />
                    <AccentPill accent={s.accent}>{s.label}</AccentPill>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {s.lines.map((l, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ArrowRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-sky-500" />
                        <span>{l}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Router map */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Braces className="h-5 w-5 text-sky-600" /> Backend Router Map
          </CardTitle>
          <CardDescription>
            Server split lives under <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono">/app/backend/routers/</code>.
            Each file mounts a sub-router at its domain-scoped prefix in <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono">server.py</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider">Router File</th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider">Domain</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {ROUTERS.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-sky-50/40"}>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-slate-800">{r.path}</td>
                    <td className="px-4 py-2.5 text-slate-700">{r.domain}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Integrations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-sky-600" /> Third-Party Integrations
          </CardTitle>
          <CardDescription>All external services touched by the platform, with their surface area.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {INTEGRATIONS.map((int) => (
              <div key={int.name} className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-sm font-black text-slate-900">{int.name}</p>
                <p className="mt-1 text-xs text-slate-600">{int.what}</p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-sky-700">Used in: <span className="normal-case font-normal text-slate-700">{int.where}</span></p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Platform highlights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-sky-600" /> Platform Highlights
          </CardTitle>
          <CardDescription>The pieces most reviewers ask about first.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {HIGHLIGHTS.map((h) => {
              const Icon = h.icon;
              return (
                <div key={h.title} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                  <Icon className="mb-2 h-5 w-5 text-sky-600" />
                  <p className="text-sm font-black text-slate-900">{h.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600">{h.body}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick-tour route list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <GitBranch className="h-5 w-5 text-sky-600" /> Suggested Walkthrough
          </CardTitle>
          <CardDescription>Click through in order for the fastest way to see the whole platform.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2">
            {[
              { path: "/admin",                    label: "1. Admin Dashboard — KPIs, campaign alerts, access requests" },
              { path: "/admin/backorders",         label: "2. Backorder Desk — hospital RFQs → supplier parallel quotes" },
              { path: "/admin/campaigns",          label: "3. Email Campaigns — 133+ manifest-driven templates" },
              { path: "/admin/crm",                label: "4. CRM — hospital + supplier + contractor contacts" },
              { path: "/admin/marketplace",        label: "5. Marketplace — B2B catalog, orders, PO uploads" },
              { path: "/admin/projects",           label: "6. Project Command Center — capital-project lifecycle" },
              { path: "/admin/trade-intelligence", label: "7. Trade Intelligence — US sea-import consignee search" },
              { path: "/solutions/general-contracting", label: "8. Healthcare GC public page — meet the team + case flow" },
            ].map((step, i) => (
              <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5">
                <span className="text-sm font-medium text-slate-800">{step.label}</span>
                <a
                  href={step.path}
                  className="inline-flex items-center gap-1 rounded bg-slate-900 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-sky-700"
                >
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Footer note */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
        <div className="flex items-start gap-3">
          <Cog className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-bold">Read-only mode is enforced server-side.</p>
            <p className="mt-1 text-xs">
              Any button that would mutate data (Save, Delete, Send, Approve, Assign) will hit the ReadOnlyDemoMiddleware
              in <code className="rounded bg-white px-1 py-0.5 text-[11px] font-mono">server.py</code> and return an HTTP 403 with{" "}
              <code className="rounded bg-white px-1 py-0.5 text-[11px] font-mono">{`{"code":"READONLY_DEMO"}`}</code>. That is by design — feel free to click things.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
